import { useCallback, useMemo, useState } from 'react';
import { DataGridCellSortState } from '@procurenetworks/procure-component-library';
import useDebounce from 'app/hooks/useDebounce';
import { ItemLocationSchema, SortOrderEnum, SortProps, UserSchema } from 'app/types/schema';
import { getPageSettingsFromStorage } from './../../../../utils/paginationSettingsUtil';
import { DeleteManagers } from '../DeleteManagersModal';
import { SnackbarService } from 'app/components/Snackbar';
import { isEmpty } from 'lodash';
import { useCategoriesTableQuery } from 'app/modules/categories/views/Categories/graphql/queries/generated/categoriesTable';
import { useSitesTableQuery } from 'app/modules/sites/views/Sites/graphql/queries/generated/sitesTable';
import { useItemsTableQuery } from 'app/modules/assetsInventory/graphql/query/generated/itemsTable';

interface State {
  activePage: number;
  search: string;
  sortState: DataGridCellSortState;
  sorts: SortProps[];
  selectedRowIds: string[];
  selectedRowData: any[];
  selection: boolean;
  multipleSelection?: boolean;
  showDeleteConfirmation: boolean;
  numberOfRowsPerPage: number;
}

interface Props {
  selection?: boolean;
  onEdit?: (id: any) => void;
  onDelete?: (id: string[], item?: object[]) => void;
  onImport?: (id: string[]) => void;
  hideActions?: boolean;
  sortState?: DataGridCellSortState;
}

const useMultiSelectEntityManager = (props: Props) => {
  const {
    onEdit: onEditProp,
    onDelete: onDeleteProp,
    selection,
    hideActions = false,
    onImport: onImportProp,
    sortState,
  } = props;

  // Read rows per page from session storage if available.
  let rowsPerPage = getPageSettingsFromStorage(10);

  const [state, _setState] = useState<State>({
    activePage: 1,
    numberOfRowsPerPage: rowsPerPage,
    search: '',
    sortState: sortState || { id: 'id', orderBy: 'asc' },
    sorts: sortState
      ? ([
          {
            sortField: sortState.id,
            sortOrder: sortState.orderBy === 'asc' ? SortOrderEnum.Asc : SortOrderEnum.Desc,
          },
        ] as SortProps[])
      : [],
    selectedRowIds: [],
    selectedRowData: [],
    showDeleteConfirmation: false,
    selection: selection !== undefined ? selection : true,
  });

  const debouncedSearchText = useDebounce(state.search);

  const [{ data: categoryManagerName }] = useCategoriesTableQuery({
    variables: {
      filters: {
        categoryManagerIds: state.selectedRowIds,
      },
    },
    requestPolicy: 'network-only',
  });

  const [{ data: siteManagerName }] = useSitesTableQuery({
    variables: {
      filters: {
        siteManagerIds: state.selectedRowIds,
      },
    },
    requestPolicy: 'network-only',
  });

  const [{ data: siteItems }] = useItemsTableQuery({
    variables: {
      filters: {
        siteIds: state.selectedRowIds,
      },
    },
    requestPolicy: 'network-only',
  });

  const deletingManagersInfo = useMemo(() => {
    const categoriesAffected = categoryManagerName?.categories.edges?.filter?.((category: any) =>
      category.node.categoryManagers.every((categoryManager: UserSchema) =>
        state.selectedRowIds.includes(categoryManager.id),
      ),
    );

    const userWithCategory: DeleteManagers[] = [];

    categoriesAffected?.forEach?.((category) =>
      category.node.categoryManagers.forEach((categoryManager: Partial<UserSchema>) => {
        userWithCategory.push({
          assignedTo: category?.node?.name,
          userName: categoryManager?.name as string,
          role: 'Category Manager',
        });
      }),
    );

    const siteAffected = siteManagerName?.locations?.edges?.filter?.((location: any) =>
      location.node.siteManagers.every((siteManager: UserSchema) =>
        state.selectedRowIds.includes(siteManager.id),
      ),
    );
    const userWithSites: DeleteManagers[] = [];
    siteAffected?.forEach?.((site) => {
      site.node.siteManagers.forEach((siteManager: Partial<UserSchema>) => {
        userWithSites.push({
          assignedTo: site.node.name,
          userName: siteManager?.name as string,
          role: 'Site Manager',
        });
      });
    });

    const itemsAffected = siteItems?.items?.edges?.filter?.((item: any) => item.node?.stockInformation?.every((itemStock: ItemLocationSchema) =>
        state.selectedRowIds.includes(itemStock?.siteId),
      )
    );

    return {
      categoryManagers: userWithCategory || [],
      siteManagers: userWithSites || [],
      items: itemsAffected || [],
    };
  }, [categoryManagerName, siteManagerName]);

  const setState = useCallback((nextState: Partial<State>) => {
    _setState((prevState) => ({ ...prevState, ...nextState }));
  }, []);

  const onSearchChange = useCallback(
    (nextSearch: string) => {
      setState({
        search: nextSearch,
        activePage: 0,
      });
    },
    [setState],
  );

  const onShowDeleteConfirmation = useCallback(() => {
      if (!isEmpty(deletingManagersInfo?.categoryManagers) || !isEmpty(deletingManagersInfo?.siteManagers) || !isEmpty(deletingManagersInfo?.items)) {
        const categoryManager = deletingManagersInfo?.categoryManagers?.[0];
        const siteManager = deletingManagersInfo?.siteManagers?.[0];
        SnackbarService.showError({
          message: categoryManager?.role ? 'PROCURE_25009' : siteManager?.role ? 'PROCURE_25008' : "PROCURE_23009",
          placeholders: {
            role:
              categoryManager?.role || siteManager?.role,
            userName:
              categoryManager?.userName || siteManager?.userName,
            assignedTo:
              categoryManager?.assignedTo || siteManager?.assignedTo,
          },
        });
      } else {
        setState({ showDeleteConfirmation: true });
      }
    },
    [setState, deletingManagersInfo],
  );

  const onCancelDelete = useCallback(() => {
    setState({ showDeleteConfirmation: false });
  }, [setState]);

  const onImport = useCallback(() => {
    if (state.selectedRowIds.length > 0 && onImportProp) {
      onImportProp(state.selectedRowIds);
    }
  }, [onImportProp, state.selectedRowIds]);

  const onEdit = useCallback(() => {
    if (state.selectedRowIds.length > 0 && onEditProp) {
      onEditProp(state.selectedRowIds);
    }
  }, [onEditProp, state.selectedRowIds]);

  const onConfirmDelete = useCallback(() => {
    if (state.selectedRowIds.length > 0) {
      if (onDeleteProp) {
        onDeleteProp(state.selectedRowIds, state.selectedRowData);
      }

      setState({ selectedRowIds: [], showDeleteConfirmation: false });
    }
  }, [onDeleteProp, state.selectedRowIds, state.selectedRowData, setState]);

  const onResetSelectedRow = useCallback(() => {
    setState({
      selectedRowIds: [],
      selectedRowData: [],
    });
  }, [setState]);

  return {
    search: {
      debouncedSearchText,
      value: state.search,
      onChange: onSearchChange,
    },
    table: {
      state: { ...state, deletingManagersInfo },
      setState,
      actions: {
        hideActions,
        onEdit: onEditProp ? onEdit : undefined,
        onCancelDelete,
        onConfirmDelete,
        onShowDeleteConfirmation,
        onResetSelectedRow,
        onImport,
      },
    },
  };
};

export type UseMultiSelectEntityManagerReturnType = ReturnType<typeof useMultiSelectEntityManager>;
export default useMultiSelectEntityManager;
